import { useState, useCallback, useEffect, useRef } from 'react';
import { LangGraphTenderAPI, StreamEvent } from '../services/langgraph';
import { AnalysisAPI } from '../services/api';
import { FormData, TenderAnalysisState } from '../types';
import { useAuth } from '../context/AuthContext';

export interface AnalysisProgress {
  currentStep: string;
  progress: number;
  message: string;
  isComplete: boolean;
  hasError: boolean;
  error?: string;
}

export interface UseTenderAnalysisReturn {
  isAnalyzing: boolean;
  progress: AnalysisProgress;
  results: TenderAnalysisState | null;
  threadId: string | null;
  startAnalysis: (formData: FormData) => Promise<{ threadId: string }>;
  resetAnalysis: () => void;
  events: StreamEvent[];
  // HITL final-approval — set when the workflow pauses at the
  // final_approval interrupt; null when no approval is pending.
  pendingFinalApproval: any | null;
  isApproving: boolean;
  approveFinalApproval: (
    decision?: 'approve' | 'reject' | 'request_revision',
    reason?: string,
  ) => Promise<void>;
  // Re-runs HITL detection for an existing thread id (post-reload).
  checkPendingApproval: (threadId: string) => Promise<void>;
}

const STEP_MAPPING: Record<string, { name: string; progress: number }> = {
  'process_tender_pdf': { name: 'Processing tender document...', progress: 10 },
  'initial_reviewer_agent': { name: 'Conducting initial review...', progress: 25 },
  'determine_refinement_path': { name: 'Determining refinement strategy...', progress: 40 },
  'extract_writing_style': { name: 'Analyzing writing style...', progress: 50 },
  'extract_successful_content_patterns': { name: 'Extracting successful patterns...', progress: 60 },
  'refine_responses_with_patterns': { name: 'Refining responses...', progress: 70 },
  'quality_check_agent': { name: 'Performing quality check...', progress: 80 },
  'generate_final_report': { name: 'Generating final report...', progress: 90 },
  'compare_score_improvement': { name: 'Comparing improvements...', progress: 95 },
  'export_response_comparison': { name: 'Finalizing analysis...', progress: 100 },
};

export function useTenderAnalysis(): UseTenderAnalysisReturn {
  const { state: authState } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [results, setResults] = useState<TenderAnalysisState | null>(null);
  const [events, setEvents] = useState<StreamEvent[]>([]);
  // HITL final-approval state. The langgraph workflow pauses at
  // `final_approval` via `interrupt(...)`; we surface that here so the
  // UI can render an Approve button. Set after the initial stream ends
  // (when the run has reached the interrupt) and cleared once resumed.
  const [pendingFinalApproval, setPendingFinalApproval] = useState<any | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [progress, setProgress] = useState<AnalysisProgress>({
    currentStep: '',
    progress: 0,
    message: 'Comprehensive tender analysis ready — Your analysis is underway — maybe a good time for a coffee or tea ☕🍵',
    isComplete: false,
    hasError: false,
  });

  // Polling fallback: while the live stream runs, fetch the persisted session
  // row every 5s and merge in any fields the stream hasn't filled yet. Belts-
  // and-braces for cases where the SDK's stream misses a subgraph update.
  const pollTickRef = useRef(0);
  useEffect(() => {
    if (!isAnalyzing || !threadId) return;
    const tid = threadId;
    let cancelled = false;

    const tick = async () => {
      pollTickRef.current += 1;
      try {
        const resp = await AnalysisAPI.getAnalysisSession(tid);
        if (cancelled || !resp.success || !resp.data) return;
        const serverResults = (resp.data as any).results;
        if (!serverResults || typeof serverResults !== 'object') return;
        setResults(prev => {
          const merged: any = { ...(prev || {}) };
          // Only fill fields that the live stream has not already provided.
          for (const [k, v] of Object.entries(serverResults)) {
            if (v === null || v === undefined) continue;
            const existing = (merged as any)[k];
            const isEmpty =
              existing === undefined ||
              existing === null ||
              (Array.isArray(existing) && existing.length === 0) ||
              (typeof existing === 'object' && !Array.isArray(existing) && Object.keys(existing).length === 0);
            if (isEmpty) {
              merged[k] = v;
            }
          }
          return merged as TenderAnalysisState;
        });
      } catch (err) {
        // Silent; next tick will retry.
      }
    };

    // Fire one immediate tick then interval.
    tick();
    const handle = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [isAnalyzing, threadId]);

  const updateProgress = useCallback((step: string, message?: string) => {
    const stepInfo = STEP_MAPPING[step] || { name: step, progress: 0 };
    setProgress(prev => ({
      ...prev,
      currentStep: step,
      progress: stepInfo.progress,
      message: message || stepInfo.name,
      isComplete: stepInfo.progress >= 100,
    }));
  }, []);

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    console.log('Stream event:', event);
    setEvents(prev => [...prev, event]);

    switch (event.event) {
      case 'values':
        // Handle state values updates - this contains the actual data
        if (event.data) {
          console.log('Values update:', event.data);
          // Update results incrementally with streaming data
          setResults(prevResults => {
            const newData = event.data as TenderAnalysisState;
            console.log('Updating results with new data:', newData);
            
            // Merge the new data with existing results
            const mergedData = {
              ...prevResults,
              ...newData,
              // Ensure arrays are properly merged/updated
              questions: newData.questions || prevResults?.questions || [],
              evaluation_criteria: newData.evaluation_criteria || prevResults?.evaluation_criteria || [],
              // Ensure other key fields are preserved
              refined_questions: newData.refined_questions || prevResults?.refined_questions || [],
              improvement_analysis: newData.improvement_analysis || prevResults?.improvement_analysis,
              comparison_content: newData.comparison_content || prevResults?.comparison_content,
              comparison_path: newData.comparison_path || prevResults?.comparison_path,
              final_report: newData.final_report || prevResults?.final_report,
              requirements_summary: newData.requirements_summary || prevResults?.requirements_summary,
              // LangExtract provenance fields
              extraction_method_used: newData.extraction_method_used || prevResults?.extraction_method_used,
              extraction_visualization_html: newData.extraction_visualization_html || prevResults?.extraction_visualization_html,
              extraction_provenance: newData.extraction_provenance || prevResults?.extraction_provenance,
              detected_tender_type: newData.detected_tender_type || prevResults?.detected_tender_type,
            };

            console.log('Merged data:', mergedData);

            // Save results to database if we have a thread ID
            if (threadId && Object.keys(newData).length > 0) {
              // Save the merged data to ensure all fields including requirements_summary are persisted
              AnalysisAPI.updateAnalysisResults(threadId, mergedData).catch(error => {
                console.error('Failed to save analysis results:', error);
              });
            }

            // Update progress message based on what we have
            if ((newData.questions?.length ?? 0) > 0 || (newData.evaluation_criteria?.length ?? 0) > 0) {
              const questionsCount = newData.questions?.length ?? 0;
              const criteriaCount = newData.evaluation_criteria?.length ?? 0;
              
              console.log('Found data:', { questionsCount, criteriaCount });
              
              let message = 'Processing document...';
              if (questionsCount > 0 && criteriaCount > 0) {
                message = `Extracted ${questionsCount} questions and ${criteriaCount} criteria`;
                // If we have both questions and criteria, consider this mostly complete
                setProgress(prev => ({
                  ...prev,
                  message: `Document processed: ${questionsCount} questions, ${criteriaCount} criteria`,
                  progress: 95,
                  isComplete: true
                }));
                
                // Stop analyzing after a short delay to show the complete state
                setTimeout(() => {
                  setIsAnalyzing(false);
                }, 1000);
              } else if (questionsCount > 0) {
                message = `Extracted ${questionsCount} questions`;
              } else if (criteriaCount > 0) {
                message = `Extracted ${criteriaCount} criteria`;
              }
              
              if (questionsCount === 0 || criteriaCount === 0) {
                setProgress(prev => ({
                  ...prev,
                  message,
                  progress: Math.min(prev.progress + 10, 90) // Gradually increase progress
                }));
              }
            }
            
            return mergedData;
          });
          
          // Don't set isAnalyzing to false here - wait for completion or error
        }
        break;
      
      case 'updates':
        // Handle step updates - this is where we can track progress
        if (event.data) {
          console.log('Step update:', event.data);
          // Look for step names in the data to update progress
          Object.keys(event.data).forEach(key => {
            if (STEP_MAPPING[key]) {
              updateProgress(key);
            }
          });
        }
        break;
      
      case 'messages':
        // Handle message updates
        console.log('Messages update:', event.data);
        break;
      
      case 'events':
        // Handle workflow events
        console.log('Events update:', event.data);
        break;
      
      default:
        console.log('Unhandled event type:', event.event);
    }
  }, [updateProgress]);

  const handleError = useCallback((error: any) => {
    console.error('Analysis error:', error);
    setProgress(prev => ({
      ...prev,
      hasError: true,
      error: error.message || 'An error occurred during analysis',
      message: 'Analysis failed',
    }));

    // Mark analysis as failed in database
    if (threadId) {
      AnalysisAPI.updateAnalysisStatus(threadId, 'failed', undefined, {
        error_message: error.message || 'An error occurred during analysis',
        error_type: error.name || 'UnknownError',
      }).catch(dbError => {
        console.error('Failed to update analysis status to failed:', dbError);
      });
    }

    setIsAnalyzing(false);
  }, [threadId]);

  const handleComplete = useCallback((finalState: TenderAnalysisState) => {
    console.log('Analysis complete:', finalState);
    setResults(finalState);
    
    // Update progress based on what data we received
    let progressMessage = 'Analysis completed successfully!';
    let progressPercent = 100;
    
    if (finalState.questions && finalState.evaluation_criteria) {
      progressMessage = `Document processed: ${finalState.questions.length} questions and ${finalState.evaluation_criteria.length} criteria extracted`;
      progressPercent = 100;
    } else if (finalState.questions) {
      progressMessage = `Document processed: ${finalState.questions.length} questions extracted`;
      progressPercent = 50;
    }
    
    setProgress(prev => ({
      ...prev,
      isComplete: true,
      progress: progressPercent,
      message: progressMessage,
    }));

    // Mark analysis as completed in database
    if (threadId) {
      AnalysisAPI.updateAnalysisStatus(threadId, 'completed').catch(error => {
        console.error('Failed to update analysis status to completed:', error);
      });

      // Save final results to database
      AnalysisAPI.updateAnalysisResults(threadId, finalState).catch(error => {
        console.error('Failed to save final analysis results:', error);
      });

      // Check whether the run actually completed or paused at the HITL
      // final_approval interrupt. If it's paused, surface that to the UI
      // so an Approve button can render. The backend pauses BEFORE running
      // compare_score_improvement and export_response_comparison — those
      // (Performance Analysis + Final Report tabs) won't populate until
      // we resume.
      LangGraphTenderAPI.getPendingFinalApproval(threadId)
        .then(payload => {
          if (payload) {
            console.log('useTenderAnalysis: run paused at final_approval HITL', payload);
            setPendingFinalApproval(payload);
          }
        })
        .catch(err => console.error('getPendingFinalApproval failed:', err));
    }

    setIsAnalyzing(false);
  }, [threadId]);

  // Resume the workflow from the final_approval HITL checkpoint. The user
  // clicks Approve in the UI → we send `Command(resume={"type": "approve"})`
  // and stream the post-approval nodes (compare_score_improvement and
  // export_response_comparison) into the same event handlers so the
  // Performance Analysis and Final Report tabs populate.
  const approveFinalApproval = useCallback(
    async (decision: 'approve' | 'reject' | 'request_revision' = 'approve', reason?: string) => {
      if (!threadId) {
        console.warn('approveFinalApproval called but no threadId set');
        return;
      }
      setIsApproving(true);
      try {
        await LangGraphTenderAPI.resumeFinalApproval(
          threadId,
          { type: decision, ...(reason ? { reason } : {}) },
          (event) => {
            setEvents(prev => [...prev, event]);
            // Re-use the same merge logic the original stream uses so
            // results state stays consistent across both phases.
            if (event.event === 'values' && event.data && typeof event.data === 'object') {
              setResults(prev => ({ ...(prev || {}), ...(event.data as any) } as TenderAnalysisState));
            }
          },
          (err) => {
            console.error('Resume final approval errored:', err);
          },
          (finalState) => {
            setResults(prev => ({ ...(prev || {}), ...(finalState as any) } as TenderAnalysisState));
          },
        );
        // Clear the pending flag — the run has resumed and is either
        // finishing or has finished.
        setPendingFinalApproval(null);
      } catch (err) {
        console.error('approveFinalApproval failed:', err);
      } finally {
        setIsApproving(false);
      }
    },
    [threadId],
  );

  const startAnalysis = useCallback(async (formData: FormData): Promise<{ threadId: string }> => {
    console.log('useTenderAnalysis: startAnalysis called with:', formData);
    
    try {
      setIsAnalyzing(true);
      setResults(null);
      setEvents([]);
      setProgress({
        currentStep: 'starting',
        progress: 0,
        message: 'Analysis beginning — Your analysis is underway — feel free to relax, we\'ve got it covered.',
        isComplete: false,
        hasError: false,
      });

      console.log('useTenderAnalysis: Calling LangGraphTenderAPI.streamTenderAnalysis...');
      const { thread_id } = await LangGraphTenderAPI.streamTenderAnalysis(
        formData,
        handleStreamEvent,
        handleError,
        handleComplete,
        authState.user?.id, // Pass the authenticated user ID
        // Expose the thread id to UI state the moment the thread is created
        // (before the long-lived stream starts iterating). Polling/URL-advance
        // effects must be able to engage mid-run, not only after the stream
        // resolves ~10 minutes later.
        (earlyId) => {
          console.log('useTenderAnalysis: onThreadCreated:', earlyId);
          setThreadId(earlyId);
          // Seed a session row immediately so polling fallback can read it.
          AnalysisAPI.getAnalysisSession(earlyId).then(existing => {
            if (!existing.success) {
              return AnalysisAPI.createAnalysisSession({
                thread_id: earlyId,
                project_id: formData.project_id,
                input_data: {
                  url: formData.url,
                  company_id: formData.company_id,
                  successful_tender_urls: formData.successful_tender_urls,
                  analysis_type: 'full_analysis',
                },
                tags: [],
              });
            }
          }).catch(err => console.warn('early session seed failed:', err));
        }
      );

      console.log('useTenderAnalysis: Received thread_id:', thread_id);
      setThreadId(thread_id);

      // Check if analysis session already exists, if not create it
      try {
        console.log('useTenderAnalysis: Checking if session exists for thread_id:', thread_id);
        const existingSession = await AnalysisAPI.getAnalysisSession(thread_id);
        
        if (existingSession.success) {
          console.log('Analysis session already exists:', existingSession.data);
        } else {
          // Session doesn't exist, create a new one
          console.log('useTenderAnalysis: Creating new analysis session...');
          const sessionResult = await AnalysisAPI.createAnalysisSession({
            thread_id: thread_id,
            project_id: formData.project_id,
            input_data: {
              url: formData.url,
              company_id: formData.company_id,
              successful_tender_urls: formData.successful_tender_urls,
              analysis_type: 'full_analysis',
            },
            tags: [],
          });

          if (sessionResult.success) {
            console.log('Analysis session created successfully:', sessionResult.data);
          } else {
            console.warn('Failed to create analysis session:', sessionResult.error);
          }
        }
      } catch (sessionError) {
        console.error('Error with analysis session:', sessionError);
        // Don't fail the entire analysis if session handling fails
      }

      return { threadId: thread_id };
    } catch (error) {
      console.error('useTenderAnalysis: Error in startAnalysis:', error);
      handleError(error);
      throw error;
    }
  }, [handleStreamEvent, handleError, handleComplete]);

  const resetAnalysis = useCallback(() => {
    setIsAnalyzing(false);
    setThreadId(null);
    setResults(null);
    setEvents([]);
    setProgress({
      currentStep: '',
      progress: 0,
      message: 'Comprehensive tender analysis ready — Your analysis is underway — maybe a good time for a coffee or tea ☕🍵',
      isComplete: false,
      hasError: false,
    });
  }, []);

  // Re-detect a pending final-approval HITL pause from an arbitrary thread
  // id. Required because the page can be re-loaded AFTER the original
  // run paused — the hook's `threadId` state is null on cold mount, so the
  // post-stream call to `getPendingFinalApproval` (line ~304) never fires.
  // The page calls this on mount with the URL/context thread id so the
  // banner re-appears across refreshes.
  const checkPendingApproval = useCallback(async (tid: string) => {
    if (!tid) return;
    try {
      // eslint-disable-next-line no-console
      console.log('[HITL] checkPendingApproval invoked with', tid);
      const payload = await LangGraphTenderAPI.getPendingFinalApproval(tid);
      // eslint-disable-next-line no-console
      console.log('[HITL] checkPendingApproval received payload:', payload);
      if (payload) {
        // Adopt the thread id so a subsequent approve call has it.
        setThreadId(tid);
        setPendingFinalApproval(payload);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[HITL] checkPendingApproval failed:', err);
    }
  }, []);

  return {
    isAnalyzing,
    progress,
    results,
    threadId,
    startAnalysis,
    resetAnalysis,
    events,
    // HITL final-approval surface — the AnalysisPage uses these to render
    // an Approve button when the run pauses at the HITL checkpoint.
    pendingFinalApproval,
    isApproving,
    approveFinalApproval,
    // Page-level reload trigger for HITL detection (see comment above).
    checkPendingApproval,
  };
}

export default useTenderAnalysis;